import {
	downloadExportDocument,
	getExportLists,
} from '@dealstack/frontend/shared/dataExchange/services/export.service';
import { useTranslationExports } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { GetTaskRes } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { downloadFileTrigger } from '@dealstack/frontend/shared/files/downloadFile';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { getExportDocumentsTableColumns } from './hooks/useExportDocumentsTableColumns';

interface DownloadTarget {
	fileKey: string;
	fileName: string;
}

export const useExportDocumentsController = () => {
	const tExports = useTranslationExports();
	const disabledButtonsRef = useRef<Set<string>>(new Set());
	const [, forceUpdate] = useState(0);
	const [downloadTarget, setDownloadTarget] = useState<DownloadTarget | null>(null);

	const downloadQuery = useQuery({
		queryKey: [
			API_PATHS.FILE_EXPORT_S3KEY_GET,
			{ s3Key: downloadTarget?.fileKey ?? '', fileName: downloadTarget?.fileName ?? '' },
		],
		queryFn: ({ signal }) =>
			downloadExportDocument(
				API_PATHS.FILE_EXPORT_S3KEY_GET,
				{ s3Key: downloadTarget?.fileKey ?? '', fileName: downloadTarget?.fileName ?? '' },
				signal
			),
		enabled: !!downloadTarget,
	});

	useEffect(() => {
		if (!downloadQuery.data?.payload?.downloadUrl) {
			return;
		}
		downloadFileTrigger(downloadQuery.data.payload.downloadUrl);
		setDownloadTarget(null);
	}, [downloadQuery.data]);

	useEffect(() => {
		if (!downloadQuery.error) {
			return;
		}
		showToastV2({ state: 'error' });
		setDownloadTarget(null);
	}, [downloadQuery.error]);

	const onDownload = useCallback((task: GetTaskRes) => {
		if (!task.fileKey || disabledButtonsRef.current.has(task.fileKey)) {
			return;
		}

		disabledButtonsRef.current.add(task.fileKey);
		forceUpdate((n) => n + 1);
		setTimeout(() => {
			if (task.fileKey) {
				disabledButtonsRef.current.delete(task.fileKey);
				forceUpdate((n) => n + 1);
			}
		}, 2000);

		const defaultFileName = `${task.projectName ?? 'unknown'}-${task.entityName ?? 'unknown'}-documents.zip`;
		setDownloadTarget({
			fileKey: task.fileKey,
			fileName: task.fileName ?? defaultFileName,
		});
	}, []);

	const columns = useMemo(
		() =>
			getExportDocumentsTableColumns({
				tExports,
				disabledButtons: disabledButtonsRef.current,
				onDownload,
			}),
		[tExports, onDownload]
	);

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<GetTaskRes, Record<string, never>>(columns, {
			initialPagination: { pageSize: 10, pageIndex: 0 },
		});

	const queryBody = useMemo(
		() => ({
			pagination: {
				take: dataOperations.pagination?.take ?? 10,
				skip: dataOperations.pagination?.skip ?? 0,
			},
			sortBy: dataOperations.sortBy?.map((s) => ({
				key: s.key === 'action' ? 'status' : s.key,
				desc: s.desc,
			})),
		}),
		[dataOperations.pagination?.take, dataOperations.pagination?.skip, dataOperations.sortBy]
	);

	const tasksRequest = useQuery({
		queryKey: [API_PATHS.TASK_POST, queryBody],
		queryFn: ({ signal }) => getExportLists(API_PATHS.TASK_POST, queryBody, signal),
		refetchInterval: 30 * 1000,
		placeholderData: keepPreviousData,
	});

	const exportTableData = tasksRequest.data?.payload;

	return {
		data: exportTableData?.record ?? [],
		columns,
		mantineTableDataOperations,
		rowCount: exportTableData?.totalRecord ?? 0,
		isLoading: tasksRequest.isLoading || tasksRequest.isFetching,
	};
};
